/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the canonical newline-split family, homed in const/ so pure utils/
 * leaves can value-import it, and the ONE home of every name in it: NO module-level mutable state -- every body here is
 * a plain transform.
 *
 * THE GROUND FOR THAT PURITY IS LEAF-LOCAL, NOT FLOOR-WIDE, and the correction is recorded rather than silently
 * swapped: this docstring claimed until 2026-09-03 that "no `const/` leaf carries module-level `let`", and that
 * claim was FALSE at HEAD with four counterexamples (`const/flow-step-output-decl-index.ts`,
 * `const/frontend-query-error-blind-sources.ts`, `const/w3c-trace-context.ts`, `const/wrangler-declared-databases.ts`)
 * until 2026-09-08, when the four were cured and the floor got its rule (`QUAL-CONST-MODULE-SCOPE-MUTABLE-BINDING-001`,
 * row 79 of the terminal arm). The correction stands in the OTHER direction too: a per-process MEMO on the const floor
 * is not an unminted class, it is an INSTITUTION with its own infrastructure and guard rules -- `memoizeByOwner`
 * (`const/memoize-by-key.ts`) owns the reassigning-WeakMap ladder under a `const` binding, `const/scan-start-cache-bus.ts`
 * fires its clear, and `QUAL-RULE-HELPER-CACHE-CLEAR-UNWIRED-001` ARM B plus `QUAL-OWNER-MEMO-CLEAR-UNREGISTERED-001`
 * convict a memo that skips the bus. What the floor forbids is the MODULE-SCOPE MUTABLE BINDING, never the memo; state
 * with a ground of its own (the realm-scoped trace id) lives off the floor. So "const/ may not hold a memo" cannot
 * carry a decline; only THIS leaf's own declared purity can, and that is a property of this leaf's bodies.
 *
 * THE SCANNER'S SINGLE-ENTRY MEMO THAT WRAPPED `splitLines` IS GONE (2026-09-25, iss-d046a223): every importer moved
 * onto this leaf and the memo was deleted, not moved. MEASURED before deleting, from the daemon's readings: a rule
 * replay runs ONE rule across many files, so a cache of the last split string hits only when a rule re-splits the
 * same text, and the rules whose readMs rose most split once per file; the file-scoped scan lane
 * (`lane-freshness-tick` -> `rule-scan`) kept its per-file cost (159 ms/file before any rule leaf swapped, 145 after),
 * so no reading showed the cache paying for itself. Grounds in the deleting commit's body.
 *
 * These bodies were the sole reason `QUAL-RAW-SPLIT-LINES-001` carried a `utils/**` exclusion: the cure it prescribed
 * was a value import of `@federa/utils`, which `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans inside `utils/`, so the
 * rule was declared UNSATISFIABLE there and amputated. That rationale never asked why an import-free split lived in
 * `utils/` at all. It now lives here, `utils/` MAY value-import `@federa/const`, and the exclusion is gone.
 */

import { hasLength } from '@federa/const/array-guards';
import { isNonEmptyString } from '@federa/const/string-guards';
import type { LineWithPredecessor } from '@federa/types/code-scanner-shapes';

/** Line `i` of `lines` with the line above it -- the `{ line, prevLine }` pair every narration and prose-window
 *  predicate reads; the first line's predecessor is the empty string.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): an index read over split lines, homed with the split family. */
export function lineWithPredecessorAt(lines: readonly string[], i: number): LineWithPredecessor {
	return { line: lines[i] ?? '', prevLine: lines[i - 1] ?? '' };
}

/** Split on `\r?\n`, so Unix and Windows endings both yield lines free of a trailing `\r` and callers need no preceding `replace(/\r/g, '')`. The returned array is frozen so a caller cannot mutate a shared result.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the base walk of this family and the cure `QUAL-RAW-SPLIT-LINES-001`
 *  prescribes; `utils/` leaves value-import it DIRECTLY, which a `utils/` home would forbid
 *  (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans a cross-utils value import), so the const floor is the only home every
 *  consumer can reach. */
export function splitLines(content: string): readonly string[] {
	return Object.freeze(content.split(/\r?\n/));
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): `content` with every CRLF rewritten to LF -- the one spelling of the
 *  line-ending normalization a CRLF-fixed migration re-read, the CRLF fixer's write and the live-schema dump normal form
 *  each re-typed. A lone CR is left alone: only the Windows pair is a line ending here. */
export function toLfLineEndings(content: string): string {
	return content.replace(/\r\n/g, '\n');
}

/**
 * THE LINES OF A TEXT WITH EACH LINE'S TRAILING WHITESPACE DROPPED.
 *
 * One decision -- "a line's trailing spaces carry no meaning" -- composed on {@link splitLines} so the normalizers
 * that depend on it agree by construction instead of by coincidence: `normalizeTransformSql`
 * (`const/telemetry-transform-drift.ts`) joins these back to compare live transform bytes against the checked-in
 * ones, and `comparableSchema` (`const/flow-host-parent-schema-scan.ts`) additionally drops the blank lines before
 * joining. Each was spelling the split-then-trimEnd pair for itself, which is how two texts normalized by two rules
 * come to disagree about equality.
 *
 * WHAT IT DOES NOT DO: it drops no blank line and trims no LEADING whitespace. Both are live distinctions at the
 * carriers above -- one keeps blanks, the other does not -- so they stay the caller's decision.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): both carriers named above are `const/` leaves, and it composes the
 * same-file {@link splitLines}.
 */
export function trimmedLines(content: string): readonly string[] {
	return splitLines(content).map((line) => line.trimEnd());
}

/** `\r` OR `\n`, either alone -- the cut {@link firstLineBeforeAnyBreak} makes, and deliberately NOT {@link splitLines}'
 *  `\r?\n`. Named here rather than at a call site for the reason {@link CARRIAGE_RETURN_CHAR_CODE} is: a line-ending
 *  literal spelled beside a consumer is a second decision about what ends a line. */
const ANY_LINE_BREAK_RE = /[\r\n]/;

/**
 * The first line of `text`, ending at the first CR **OR** LF, and the WHOLE string when it carries neither.
 *
 * THE LONE `\r` IS THE WHOLE REASON THIS IS NOT `splitLines(text)[0]`. A CR-only body reads as ONE line under a
 * `\r?\n` split, so its entire text would be measured as the commit SUBJECT -- and the commit-subject-length gate
 * would then refuse a command over bytes no subject contains. The two cuts agree on every LF and CRLF input and
 * disagree only there, which is exactly why the difference has to be a NAMED decision instead of a spelling choice
 * a reader has to reconstruct.
 *
 * ONE HOME FOR THE CUT: every reader that needs a first line takes it from here, so no two copies of the `search` +
 * `slice` body can come to disagree about where a line ends (the `QUAL-PRIVATE-FN-SHADOWS-SHARED-EXPORT-001` shape).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): a line-cut DECISION, homed beside the split it is deliberately
 * not, because its consumers straddle `const/`, `utils/`, `spa/` and `tools/` leaves, and a `utils/` leaf may not
 * value-import a `utils/` sibling.
 */
export function firstLineBeforeAnyBreak(text: string): string {
	const brk = text.search(ANY_LINE_BREAK_RE);
	return brk === -1 ? text : text.slice(0, brk);
}

/** `\r` -- the only glyph {@link splitLines} can consume ahead of the `\n`, so a two-byte terminator is distinguishable
 *  from a one-byte one without any consumer spelling a line-ending literal of its own. */
export const CARRIAGE_RETURN_CHAR_CODE = 0x0d;

/**
 * The terminator {@link splitLines} CONSUMED after the line starting at `lineStart` and running `lineLength` bytes --
 * `'\r\n'`, `'\n'`, or `''` at end of input. The INVERSE of the split, and it exists because the split is LOSSY in a
 * way that silently breaks every offset-preserving projection built on it: `splitLines` drops the `\r`, so a walk that
 * re-joins its result on a bare `'\n'` returns a CRLF source SHORTER than it arrived, and every offset a caller then
 * maps back onto the raw content is one byte early per preceding line. Witnessed as exactly that, and cured with this
 * helper, in `walkCommentSpans` (`const/comment-detection.ts`) -- the walk behind `maskCommentsPreservingOffsets`,
 * `maskCommentsAndTrailingPreservingOffsets` and `commentTextOnly`, three exports whose NAMES promise the offsets the
 * re-join was discarding.
 *
 * THE SECOND CARRIER IS ALSO CURED (2026-08-24): `stripStringLiteralsBlankPreservingLines`
 * (`tools/code-scanner/compliance/rule-helpers/strip-string-literals.ts`) carried the identical `splitLines` +
 * `join('\n')` shape under an identical name-promise, and now applies this helper exactly as the comment walk does.
 * Its deferral is recorded here because the deferral REASON generalizes to every future consumer outside this package:
 * a worktree-authored import of a `@federa/const` export resolves to the MAIN checkout, so the export must already be
 * merged or every worktree replay of a rule transitively importing that leaf fails to link. The class this helper
 * governs is mechanized by `QUAL-OFFSET-PRESERVING-WALK-REJOINS-NEWLINE-001`, which is where a new carrier is caught.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the terminator vocabulary is a DECISION, and homing it beside the
 * split it inverts is what stops a consumer re-deciding what a line ending is. Consumers straddle `const/` and the
 * `tools/` rule-helper floor, and the const floor is the only home each can reach.
 */
export function lineTerminatorAfter(content: string, lineStart: number, lineLength: number): string {
	const at = lineStart + lineLength;
	if (at >= content.length) return '';
	return content.charCodeAt(at) === CARRIAGE_RETURN_CHAR_CODE ? content.slice(at, at + 2) : content.slice(at, at + 1);
}

/** `\n` -- the byte {@link splitLines} breaks on and the only one {@link lineStartOffsets} counts, named for the same
 *  reason {@link CARRIAGE_RETURN_CHAR_CODE} is: a line-ending literal spelled at a call site is a second decision. */
export const LINE_FEED_CHAR_CODE = 0x0a;

/**
 * Byte offset each line of `content` starts at. Built ONCE by a caller that then maps many absolute offsets back to
 * lines, or splices a line span out of the raw string, without re-scanning per hit. Always starts with 0, so an empty
 * string yields `[0]` and offset 0 resolves to line 1; a trailing newline yields a final start past the end, which is
 * the position {@link splitLines}' own trailing empty line begins at.
 *
 * LF-KEYED, and deliberately so: the offsets index the RAW content, so a `\r\n` file yields the same starts as its
 * `\n` twin and the `\r` merely rides at the end of the preceding line.
 *
 * THIS IS THE WHOLE FAMILY: one body computes this vector. A private walk over the ALREADY-SPLIT lines, terminator by
 * terminator, answers the SAME contract rather than a different one: for `lines = splitLines(content)` the two walks
 * are EQUAL element-for-element and equal in length, because `splitLines` breaks at every `\n` (so it yields
 * `1 + count('\n')` entries, exactly what the seed-plus-push walk below yields) and `lineTerminatorAfter` returns
 * precisely the bytes the split consumed. A second copy held apart only by an assertion of difference is the
 * `QUAL-PRIVATE-FN-SHADOWS-SHARED-EXPORT-001` shape; the equivalence is PINNED in `const/__tests__/line-splitting.test.ts`
 * against a reference spelling of the two-argument contract, so a future edit that makes them diverge reds rather than
 * silently re-splitting the family.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the offsets index of the same-file {@link splitLines}, homed on the
 * const floor because its consumers straddle layers that cannot reach one another -- the `tools/` rule leaves and
 * the `utils/` compliance rule-helpers, which `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids from value-importing any
 * `utils/` sibling.
 */
export function lineStartOffsets(content: string): readonly number[] {
	const starts: number[] = [0];
	for (let i = 0; i < content.length; i++) {
		if (content.charCodeAt(i) === LINE_FEED_CHAR_CODE) starts.push(i + 1);
	}
	return starts;
}

/**
 * A reader of LOOK-AHEAD WINDOW ends over one text: the returned function answers where the window that opens on the
 * 1-based line `anchorLine` and runs `spanLines` lines past it ENDS -- the end of that last line (clamped to the text's
 * last line), before its terminator, so a CRLF line's `\r` stays outside as it does in {@link splitLines}. Built once
 * per text so a per-candidate call splits nothing; the two throwing-guard rules each computed this end inline.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link splitLines} and
 * {@link lineStartOffsets}, homed with them on the floor every rule layer reaches.
 */
export function lineWindowEnds(text: string): (anchorLine: number, spanLines: number) => number {
	const lines = splitLines(text);
	const starts = lineStartOffsets(text);
	return (anchorLine, spanLines) => {
		const lastLine = Math.min(lines.length, anchorLine + spanLines);
		return (starts[lastLine - 1] ?? text.length) + (lines[lastLine - 1] ?? '').length;
	};
}

/**
 * The offset the line holding `position` starts at -- one past the newline before it, or 0 on the first line. The
 * per-position question {@link lineStartOffsets} answers for every line at once, for a caller holding ONE offset.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): read by the record-leaf rewrite splice
 * (`const/record-leaf-rewrite-splice.ts`) and the comment-preservation walk
 * (`const/record-comment-preservation.ts`), homed with the split family on the floor both reach.
 */
export function lineStartAt(text: string, position: number): number {
	return text.lastIndexOf('\n', position - 1) + 1;
}

/**
 * The offset the line holding `position` ENDS at -- its terminating newline, or `text.length` on the last line. The
 * other bound of {@link lineStartAt}, so `text.slice(lineStartAt(t, p), lineEndAt(t, p))` is that line (a CRLF line
 * keeps its `\r`, as every raw-offset walk here does).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the `indexOf('\n', from)`-or-length read the violation snippet
 * builders and the shell-command segmenter each spelled inline, homed with its sibling on the floor both reach.
 */
export function lineEndAt(text: string, position: number): number {
	const newline = text.indexOf('\n', position);
	return newline < 0 ? text.length : newline;
}

/**
 * Each LF-delimited segment of `content` paired with the byte offset it STARTS at, `\r` retained on the line -- the
 * offset-preserving walk, and the member `QUAL-RAW-SPLIT-LINES-001` prescribes wherever a raw `content.split('\n')`
 * was load-bearing rather than lazy.
 *
 * THE `\r` IS THE WHOLE REASON THIS IS NOT {@link splitLines}, and the two sites that had this body privately each
 * said so in their own words: a caller advancing `offset += line.length + 1` and then slicing the RAW content at that
 * offset is exact only while a CRLF line keeps its `\r` inside `line`. `splitLines` strips it, so every subsequent
 * offset drifts one byte per preceding CRLF line and the caller reads a window shifted off the span it is testing.
 * The line COUNT is identical either way; it is the OFFSETS that break.
 *
 * FOLDED HERE 2026-09-05 (SSOT round 9, rules 5a lane) from two `pure/` rule leaves that had each spelled the walk
 * inline -- `QUAL-PRODUCER-BOUND-OPTIONAL-MEMBER-001` (return-type header scan) and `QUAL-WHY-REASK-UNSTAMPED-001`
 * (comment-run span builder), the second's comment openly citing the first's as "the same adjudicated decline". Two
 * copies of one decision, and TWO copies of the rationale prose defending it, is the shape a named home ends: each
 * stood as an un-drainable row of `QUAL-RAW-SPLIT-LINES-001` because the cure that rule names is the WRONG one here,
 * and a rule row nothing can drain is a standing triage tax rather than a queue.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composed from the same-file {@link lineStartOffsets} rather than
 * from a second raw split, so the offsets a caller receives and the offsets every other consumer of that index
 * receives are the SAME vector by construction. Element-for-element equal to `content.split('\n')` -- pinned in
 * `const/__tests__/line-splitting.test.ts` against that reference spelling, empty input and CRLF included.
 */
export function rawLineSpans(content: string): readonly { line: string; start: number }[] {
	const starts = lineStartOffsets(content);
	return starts.map((start, index) => ({ line: content.slice(start, (starts[index + 1] ?? content.length + 1) - 1), start }));
}

/** Trimmed, non-blank lines of `content` -- the split + trim + drop-blank chain every command-output and ledger reader repeats verbatim. Returns a FRESH array, so callers may mutate it (unlike {@link splitLines}, which is frozen).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link splitLines} and rides its home, so
 *  the `utils/` consumer reaches both through one `@federa/const` import rather than a banned utils sibling. */
export function splitNonEmptyLines(content: string): string[] {
	return trimmedNonBlank(splitLines(content));
}

/** Every piece trimmed, the ones left empty dropped, order kept -- the tail of every split-on-a-separator reader (line
 *  splits above, and the union-arm and argument-list splitters in `utils/compliance/rule-helpers/`). FRESH array.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): its `utils/` readers may not share a utils sibling. */
export function trimmedNonBlank(pieces: readonly string[]): string[] {
	return pieces.map((piece) => piece.trim()).filter(hasLength);
}

/** {@link splitNonEmptyLines} over a command's stdout that may be absent: `null` -- the host could not ask the question
 *  (`tryRunGitCore`'s shape) -- answers no lines rather than a throw.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link splitNonEmptyLines}; same `utils/` reach. */
export function nonEmptyLinesOrNone(stdout: string | null): string[] {
	return stdout === null ? [] : splitNonEmptyLines(stdout);
}

/** The NON-BLANK lines of `content` AS WRITTEN -- blank and whitespace-only lines dropped, every kept line's own leading
 *  and trailing whitespace left alone. The untrimmed sibling of {@link splitNonEmptyLines}: a reader that re-emits or
 *  byte-compares its lines (a status answer's indented JSON, a journal's rows, a diff's sides) must not have them
 *  rewritten, and the blank drop is its only filter. Returns a FRESH array.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link splitLines}; same `utils/` reach. */
export function nonBlankLines(content: string): string[] {
	return splitLines(content).filter(isNonEmptyString);
}

/** Each row under a message's lead line, indented two spaces -- the item list a hook's BLOCK text sets between its
 *  verdict line and its cure line. Returns a FRESH array.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): pure over its input; the `utils/` hook cores reach it here. */
export function indentedRows(rows: readonly string[]): string[] {
	return rows.map((row) => `  ${row}`);
}

/** Index of the first line at or after `from` carrying non-whitespace text, or `lines.length` when none does -- the
 *  blank-row skip the declaration walkers each spelled as a `while (i < lines.length && ... .trim() === '') i++`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): pure over its inputs; same `utils/` reach as its siblings. */
export function nextNonBlankLineIndex(lines: readonly string[], from: number): number {
	let index = from;
	while (index < lines.length && !isNonEmptyString(lines[index])) index++;
	return index;
}

/** Line count; empty content is 0, any non-empty content at least 1.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): counts the same-file {@link splitLines} result, so the count and the
 *  split can never disagree about what a line is; const-homed for the `utils/` consumers the utils ban closes out. */
export function countLines(content: string): number {
	if (content.length === 0) {
		return 0;
	}
	return splitLines(content).length;
}

/**
 * Lines back to ONE newline-terminated text, and the EMPTY set answers the empty string rather than a bare `'\n'`.
 *
 * THE EMPTY ARM IS THE WHOLE DECISION, which is why this is a named member and not a `join` at a call site. Both
 * carriers append their result to a file every later reader splits again: a bare `'\n'` for "nothing to write" seeds a
 * blank first record, and on the journal ladder that blank is then concatenated ahead of the next generation's first
 * line. The join itself is trivial; agreeing on what NOTHING renders as is not.
 *
 * FOLDED HERE 2026-09-06 (SSOT round 15, utils d-g lane) from two byte-identical bodies that had each decided it
 * privately -- linesToText in `utils/fs/journal-generations-read.ts` (the journal ladder's text answer, on both the
 * whole-read and the incremental arm) and the tail of `trimGenerationJournal` in `utils/dev/generation-rendezvous.ts`
 * (the generation journal's trim). The two sit on OPPOSITE sides of one habit -- a reader of a rotated JSONL sink and a
 * writer that bounds one -- so a drift at either would have been a disagreement about whether an empty sink is a file
 * with no bytes or a file with one blank line, decided differently by the reader and the writer.
 *
 * NOT AN INVERSE OF {@link splitLines} AND DOES NOT CLAIM TO BE: the split yields a trailing empty entry for a
 * terminated text, so re-joining its raw result here would add a terminator the source did not carry. It is the join
 * for a set of lines a caller has already decided are records -- both carriers filter blanks first.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): a line-ending DECISION, homed beside the split family that owns
 * every other one. Both consumers are pure `utils/` leaves that may not value-import a `utils/` sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`), so the const floor is the only home either can reach.
 */
export function joinLinesTerminated(lines: readonly string[]): string {
	return lines.length === 0 ? '' : `${lines.join('\n')}\n`;
}

/** `head`, then `following` on its own line when it is non-empty -- a message and its stack, a reason and its stderr
 *  tail. An absent or empty detail answers the bare head, never a head with a dangling newline.
 *  @runtime-derived-ssot-helper (utils-floor-pinned): `utils/errors/error-core.ts` and
 *  `utils/linting/vitest-nonrun-classify.ts` value-bind it. */
export function withFollowingLine(head: string, following: string | undefined): string {
	return following ? `${head}\n${following}` : head;
}
